import { cn } from "#/lib/utils";

type LeverageSliderProps = {
	marks: number[];
	selectedIndex: number;
	onIndexChange: (index: number) => void;
	formatLabel?: (value: number) => string;
};

function defaultFormat(v: number) {
	return `${v}x`;
}

export function LeverageSlider({
	marks,
	selectedIndex,
	onIndexChange,
	formatLabel = defaultFormat,
}: LeverageSliderProps) {
	const safeIndex = Math.min(Math.max(selectedIndex, 0), marks.length - 1);
	const pct = marks.length > 1 ? (safeIndex / (marks.length - 1)) * 100 : 0;

	function handleChange(rawPct: number) {
		const index = Math.round((rawPct / 100) * (marks.length - 1));
		onIndexChange(index);
	}

	return (
		<div className="relative px-1 pt-3 pb-4">
			<div className="relative h-4">
				{/* Background track */}
				<div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-trade-border" />

				{/* Filled track */}
				<div
					className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-trade-text-secondary"
					style={{ width: `${pct}%` }}
				/>

				{/* Diamond step markers */}
				{marks.map((mark, i) => {
					const pos = marks.length > 1 ? (i / (marks.length - 1)) * 100 : 0;
					const isActive = i <= safeIndex;
					// Theme color tokens are semi-transparent: drawing the dot straight
					// onto the same-colored (also semi-transparent) track compounds the
					// overlap darker, so the line reads as passing in front of the dot.
					// Compositing the color over an opaque surface base keeps the dot
					// opaque so it covers the line beneath it and sits on top.
					const dotColor = isActive
						? "var(--color-trade-text-secondary)"
						: "var(--color-trade-border)";

					return (
						<button
							key={mark}
							type="button"
							onClick={() => onIndexChange(i)}
							className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer p-1"
							style={{ left: `${pos}%` }}
						>
							<span
								className="block h-2.5 w-2.5 rotate-45 rounded-[2px] border"
								style={{
									backgroundColor: "var(--color-trade-surface)",
									backgroundImage: `linear-gradient(${dotColor}, ${dotColor})`,
								}}
							/>
							<span
								className={cn(
									"absolute left-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap text-caption1",
									isActive
										? "text-trade-text-secondary"
										: "text-trade-text-faint",
								)}
							>
								{formatLabel(mark)}
							</span>
						</button>
					);
				})}

				{/* Thumb */}
				<div
					className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
					style={{ left: `${pct}%` }}
				>
					<span className="block h-4.5 w-4.5 rounded-full border-[3px] border-trade-text-secondary bg-trade-surface shadow-sm" />
				</div>

				{/* Hidden range input for drag */}
				<input
					type="range"
					min={0}
					max={100}
					step={1}
					value={pct}
					onChange={(e) => handleChange(Number(e.target.value))}
					className="absolute inset-x-0 h-full cursor-pointer opacity-0"
				/>
			</div>
		</div>
	);
}
